/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {StateService} from '../core/state/state.service';
import { VehiclePortfolioLoadResult } from '../models/vehicle-portfolio-load-result.model';
import { VehiclePortfolioLoadViolation } from '../models/vehicle-portfolio-load-violations.model';
import { VehiclePortfolioLoad } from '../models/vehicle-portfolio-load.model';
import { ConfigService } from './config.service';
import { ExcelExportService } from './excel-export.service';

@Injectable({
  providedIn: 'root'
})
export class VehiclePortfolioLoadService extends ExcelExportService {

  private readonly gmid: string;

  constructor(protected readonly configService: ConfigService,
              protected readonly http: HttpClient,
              private readonly userService: StateService) {
    super(configService, http);
    // Set the endpoint for the ExcelExportService
    this.baseEndpoint = `${this.configService.baseApiUrl}` + 'portfolio/vehicle';
    this.gmid = this.userService.user.gmid;
  }

  /**
   * Loads a new vehicle portfolio and returns the expected Arrays
   * @param entries entries to be loaded
   * @returns observable of VehiclePortfolioLoadResult
   */
  loadPortfolio(entries: Map<string, VehiclePortfolioLoad[]>): Observable<Map<string, VehiclePortfolioLoadResult>> {
    return this.http.post<Map<string, VehiclePortfolioLoadResult>>(this.baseEndpoint, entries, {
      params: new HttpParams().set('gmid', this.gmid)
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Validates a Vehicle Portfolio load
   * @param entries entries to be validated
   * @returns object containing arrays of entries with violations
   */
  validatePortfolioLoad(entries: Map<string, VehiclePortfolioLoad[]>): Observable<Map<string, VehiclePortfolioLoadViolation>> {
    return this.http.post<Map<string, VehiclePortfolioLoadViolation>>(this.baseEndpoint + '/validate', entries)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Get the current portfolio by port type
   * @param portType portfolio type
   */
  getCurrentPortfolioByPortType(portType: string): Observable<any> {
    const params = new HttpParams().set('portType', portType);
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/vehicle/current-portfolio`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Transforms an Excel file and specified list of Sheets into a map of Sheet names to portfolio data
   *
   * @param file Excel file
   * @param sheetList List of Sheet names
   */
  transformToModel(file: File, sheetList: string[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    return this.http.post<any>(this.baseEndpoint + '/transform', formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Service call to update transfer program codes from a business plan portfolio to a TVP portfolio
   * @param recordsToTransfer the vehicle portfolio records to transfer
   */
  businessPortfolioTransfer(recordsToTransfer: VehiclePortfolioLoad[]): Observable<Map<string, VehiclePortfolioLoadResult>>  {
    return this.http.post<Map<string, VehiclePortfolioLoadResult>>(this.baseEndpoint + '/portfolio-transfer', recordsToTransfer, {
      params: new HttpParams().set('gmid', this.gmid)
    }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Get all archived portfolios
   */
  getArchivedPortfolios(): Observable<any> {
    return this.http.get<any>(`${this.configService.baseApiUrl}portfolio/vehicle/portfolio-archive/list`)
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Archive the current portfolio
   * @param archiveName the name of the archive
   * @param gmid the GMID of the user
   */
  archiveCurrentPortfolio(archiveName: string, gmid: string): Observable<any> {
    const params = new HttpParams().set('archiveName', archiveName).set('gmid', gmid);
    return this.http.post<any>(`${this.configService.baseApiUrl}portfolio/vehicle/portfolio-archive`, null, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Update the name of an archive
   * @param oldName the name of the archive
   * @param newName the new name of the archive
   */
  updateArchiveName(oldName: string, newName: string): Observable<any> {
    const params = new HttpParams().set('oldName', oldName).set('newName', newName);
    return this.http.put<any>(`${this.configService.baseApiUrl}portfolio/vehicle/portfolio-archive`, null, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  /**
   * Delete an archive
   * @param archiveName the name of the archive
   */
  deleteArchive(archiveName: string): Observable<any> {
    const params = new HttpParams().set('archiveName', archiveName);
    return this.http.delete<any>(`${this.configService.baseApiUrl}portfolio/vehicle/portfolio-archive`, {params})
    .pipe(catchError(myError => this.handleError(myError)));
  }

  private handleError(errorResponse: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unexpected error occurred';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }
}
